import { useEffect, useRef } from 'react';
import { Avatar } from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { ChannelMemberOption } from '../../services/api';
import { MOTION } from './chatMotion';
import { initialsOf } from './initials';

interface MentionListProps {
  id: string;
  rows: ChannelMemberOption[];
  status: 'loading' | 'ready' | 'error';
  highlighted: number;
  touch: boolean;
  onPick: (member: ChannelMemberOption) => void;
}

const EMPTY_COPY = {
  ready: 'No members match',
  error: 'Could not load members',
} as const;

// The text area must keep focus, so a press on the list never reaches it.
const keepFocus = (event: { preventDefault: () => void }) =>
  event.preventDefault();

export function MentionList({
  id,
  rows,
  status,
  highlighted,
  touch,
  onPick,
}: MentionListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the highlighted row inside the list's own scroll area. Only the list
  // scrolls, and the text area keeps focus.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !row) return;
    if (row.offsetTop < list.scrollTop) {
      list.scrollTop = row.offsetTop;
    } else if (
      row.offsetTop + row.offsetHeight >
      list.scrollTop + list.clientHeight
    ) {
      list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
    }
  }, [highlighted, rows]);

  return (
    <div
      ref={listRef}
      id={id}
      role='listbox'
      onMouseDown={keepFocus}
      onPointerDown={keepFocus}
      // A container, so a row can size its disciplines to the list width.
      className={`absolute bottom-full left-0 right-0 z-10 mb-1 overflow-y-auto rounded-md border bg-background shadow [container-type:inline-size] ${
        touch ? 'max-h-44' : 'max-h-60'
      } ${MOTION.fade}`}
    >
      {rows.length === 0 ? (
        status === 'loading' ? (
          <div
            role='status'
            aria-label='Loading members'
            className='flex justify-center px-3 py-2'
          >
            <Loader2
              className='h-4 w-4 animate-spin text-muted-foreground'
              aria-hidden
            />
          </div>
        ) : (
          <div className='px-3 py-2 text-sm text-muted-foreground'>
            {EMPTY_COPY[status]}
          </div>
        )
      ) : (
        rows.map((member, index) => (
          <div
            key={member.id}
            id={`${id}-${member.id}`}
            role='option'
            aria-selected={index === highlighted}
            onMouseDown={keepFocus}
            onPointerDown={keepFocus}
            onClick={() => onPick(member)}
            className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${
              touch ? 'min-h-11' : ''
            } ${index === highlighted ? 'bg-muted' : 'hover:bg-muted'}`}
          >
            <Avatar size='sm' initials={initialsOf(member.name)} />
            <span className='min-w-0 flex-1 truncate text-sm'>
              {member.name}
            </span>
            {member.disciplines && member.disciplines.length > 0 && (
              <span className='shrink-0 text-xs text-muted-foreground'>
                {/* A narrow list (the normal window, the phone sheet) keeps
                    room for the name: the first discipline, then …. */}
                <span className='[@container(min-width:25rem)]:hidden'>
                  {member.disciplines.length > 1
                    ? `${member.disciplines[0]} …`
                    : member.disciplines[0]}
                </span>
                <span className='hidden [@container(min-width:25rem)]:inline'>
                  {member.disciplines.join(' · ')}
                </span>
              </span>
            )}
          </div>
        ))
      )}
    </div>
  );
}
